import {
  extensionExists,
  getFirstExtension,
} from '../unsw-fhir/Extension/helpers';
import {
  cristalHasAnswerExtension,
  cristalGreaterOneExtension,
  cristalValueExtension,
  cristalOver65Extension,
} from './Extensions';
import { IQuestionnaireResponse } from '../unsw-fhir/QuestionnaireResponse';
import QuestionnaireMutator from '../unsw-fhir-questionnaire/Utilities/QuestionnaireMutator';
import { IQuestionnaire } from '../unsw-fhir/Questionnaire';
import {
  isQuestionnaireResponseItemAnswer,
  isCodingAnswerComponent,
} from '../unsw-fhir/QuestionnaireResponse/typeGuards';

const walk = (
  func: (prev: any, val: any) => number | null = () => null,
  initial: number | null = null,
  item: any,
  ..._rest: any[]
) => {
  const { item: items } = item || { item: undefined };
  if (!items) {
    return func(initial, item);
  }
  return items.reduce((prev: any, val: any, ...args: any[]) => {
    const score = walk(func, prev, val, ...args);
    if (extensionExists(val, cristalHasAnswerExtension)) {
      const dScore = score - prev;
      if (dScore > 0) {
        const ext = getFirstExtension(val, cristalHasAnswerExtension);
        const eVal = ext ? ext.valueDecimal || 0 : 0;
        return prev + eVal;
      }
      return prev;
    }

    if (extensionExists(val, cristalGreaterOneExtension)) {
      const dScore = score - prev;
      if (dScore > 1) {
        const ext = getFirstExtension(val, cristalGreaterOneExtension);
        const eVal = ext ? ext.valueDecimal || 0 : 0;
        return prev + eVal;
      }
      return prev;
    }
    return score;
  }, initial);
};

export const calculateQuestionnaireScore =
  (func: any, initial: number) =>
  (questionnaireResponse: IQuestionnaireResponse) =>
    walk(func, initial, questionnaireResponse);

export const calculateCriSTALScore = (
  questionnaireResponse: IQuestionnaireResponse,
) => {
  const scoringFunc = (prev: number, val: any): number => {
    if (!val) return prev;
    const { answer } = val;
    const { valueCoding } = val;

    if (answer) {
      if (extensionExists(val, cristalOver65Extension)) {
        if (answer.length > 0) {
          const ext = getFirstExtension(val, cristalOver65Extension);
          const lScore =
            ext &&
            ext.valueBoolean &&
            val.answer.some((v: any) => v.valueBoolean)
              ? 1
              : 0;
          return prev + lScore;
        }
      }

      if (extensionExists(val, cristalHasAnswerExtension)) {
        if (answer.length > 0) {
          const ext = getFirstExtension(val, cristalHasAnswerExtension);
          const lScore = ext ? ext.valueDecimal || 0 : 0;
          return prev + lScore;
        }
        return prev;
      }

      if (extensionExists(val, cristalGreaterOneExtension)) {
        if (answer.length > 0) {
          return prev +
            answer.reduce(
              (p: number, v: any) => scoringFunc(p, v || {}),
              prev,
            ) >
            1
            ? 1
            : 0;
        }
        return prev;
      }
      return answer.reduce((p: any, v: any) => scoringFunc(p, v || {}), prev);
    }

    if (valueCoding && extensionExists(valueCoding, cristalValueExtension)) {
      const ext = getFirstExtension(valueCoding, cristalValueExtension);
      const lScore = ext ? ext.valueDecimal || 0 : 0;
      return prev + lScore;
    }

    return prev;
  };

  return calculateQuestionnaireScore(scoringFunc, 0)(questionnaireResponse);
};

export const getConditionCodesFromCristal = (
  questionaire: IQuestionnaire,
  response: IQuestionnaireResponse,
) => {
  const mutator = new QuestionnaireMutator(questionaire, response);
  const container = mutator.getQuestionnaireResponse('3-1');

  let codes: string[] = [];
  if (container) {
    const { value: item } = container;
    if (isQuestionnaireResponseItemAnswer(item))
      codes = item.answer
        .map(answer =>
          isCodingAnswerComponent(answer) ? answer.valueCoding.code : undefined,
        )
        .filter((answer): answer is string => !!answer);
  }
  return codes;
};
